import { useState, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';
import { mapToBarberProfile, BarberProfile } from '../lib/supabase-mapping';
import { BusinessSignupData } from '../components/onboarding/magic-steps/BusinessSignup';
import { toast } from 'sonner';
import { debugLog } from '@/lib/debug-logger';

export const useBusinessProfile = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Accepts the v2 draft shape: BusinessSignupData plus the optional theme key
  // chosen before signup. v1 callers pass plain BusinessSignupData.
  const saveBusinessProfile = async (data: BusinessSignupData & { bookingFlowThemeKey?: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Map magic-onboarding data to existing Supabase schema
      const barberProfile = mapToBarberProfile(data);

      // 0. Upload logo if provided
      let logoUrl = data.logoPreview;
      if (data.logoFile) {
        try {
          // Validate file type
          const validExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
          const fileExt = data.logoFile.name.split('.').pop()?.toLowerCase();

          if (!fileExt || !validExtensions.includes(fileExt)) {
            throw new Error('Invalid file type. Please upload PNG, JPG, GIF, or WebP.');
          }

          // Validate file size (max 2MB)
          const maxSize = 2 * 1024 * 1024; // 2MB in bytes
          if (data.logoFile.size > maxSize) {
            throw new Error('Logo must be under 2MB.');
          }

          const fileName = `${user.id}/logo-${Date.now()}.${fileExt}`;

          const { error: uploadError } = await supabase.storage
            .from('business-logos')
            .upload(fileName, data.logoFile);

          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage
              .from('business-logos')
              .getPublicUrl(fileName);
            logoUrl = publicUrl;
            toast.success('Logo uploaded successfully!');
          } else {
            console.error('Error uploading logo:', uploadError);
            toast.error('Failed to upload logo. Please try again.');
            // Don't throw - logo is optional, continue with onboarding
          }
        } catch (error: any) {
          console.error('Logo validation error:', error);
          toast.error(error.message || 'Failed to upload logo');
          // Don't throw - logo is optional, continue with onboarding
        }
      }

      // 1. Create or update business profile
      // Avoid blind upsert — use SELECT first, then INSERT or UPDATE on the
      // canonical row. This is defensive: a UNIQUE constraint on user_id now
      // exists (added by 20260428040000 migration), but selecting by id for the
      // UPDATE is more precise than relying on constraint inference.
      const businessProfilePayload = {
        user_id: user.id,
        name: barberProfile.businessName,
        email: barberProfile.email,
        phone: barberProfile.phone,
        address: barberProfile.businessAddress,
        suite_number: barberProfile.suiteNumber || null,
        timezone: barberProfile.timezone,
        business_type: barberProfile.businessType === 'multi' ? 'multi' : 'solo',
        booking_url: barberProfile.bookingUrl,
        welcome_message: barberProfile.welcomeMessage,
        logo_url: logoUrl,
        stripe_connected: barberProfile.stripeConnected,
        notifications_enabled: barberProfile.notificationsEnabled,
        admin_cancellation_notifications_enabled: barberProfile.adminCancellationNotificationsEnabled,
        reminders_enabled: barberProfile.remindersEnabled,
        google_calendar_connected: barberProfile.googleCalendarConnected || false,
        website: barberProfile.bookingUrl,
        instagram: barberProfile.instagram || '',
        title: barberProfile.title ?? null,
        bio: barberProfile.bio ?? null,
        // Chosen in the pre-signup preview; drives both the link-in-bio and the
        // booking flow on the published page.
        booking_flow_theme_key: barberProfile.bookingFlowThemeKey || 'figgy_purple',
      };

      // Check if this user already has a business profile
      const { data: existingRows } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);
      const existingProfile = existingRows?.[0] ?? null;

      let businessProfile: any;
      if (existingProfile) {
        // UPDATE the existing row
        const { data, error: profileError } = await supabase
          .from('business_profiles')
          .update(businessProfilePayload)
          .eq('id', existingProfile.id)
          .select()
          .single();
        if (profileError) {
          console.error('=== BUSINESS_PROFILES UPDATE ERROR ===', profileError);
          throw profileError;
        }
        businessProfile = data;
        console.log('=== BUSINESS_PROFILES UPDATED, id:', businessProfile.id);
      } else {
        // INSERT a new row
        const { data, error: profileError } = await supabase
          .from('business_profiles')
          .insert(businessProfilePayload)
          .select()
          .single();
        if (profileError) {
          console.error('=== BUSINESS_PROFILES INSERT ERROR ===', profileError);
          throw profileError;
        }
        businessProfile = data;
        console.log('=== BUSINESS_PROFILES INSERTED, id:', businessProfile.id);
      }

      // 2. Save services (non-blocking - log errors but don't throw)
      if (barberProfile.services.length > 0) {
        const servicesPayload = barberProfile.services.map(service => ({
          business_id: businessProfile.id,
          name: service.name,
          description: service.description,
          duration: service.duration,
          price: service.price,
          waitlist_enabled: service.waitlistEnabled,
          buffer_time: service.bufferTime
        }));
        
        console.log('=== BUSINESS_SERVICES PAYLOAD ===');
        console.log(JSON.stringify(servicesPayload, null, 2));
        
        const { error: servicesError } = await supabase
          .from('business_services')
          .upsert(servicesPayload);

        if (servicesError) {
          console.error('=== BUSINESS_SERVICES ERROR ===');
          console.error('Error code:', servicesError.code);
          console.error('Error message:', servicesError.message);
          console.error('Full error object:', JSON.stringify(servicesError, null, 2));
          // Don't throw - services can be added later
        } else {
          console.log('=== BUSINESS_SERVICES SUCCESS ===');
        }
      }

      // 3. Save working hours (non-blocking - log errors but don't throw)
      if (barberProfile.workingHours) {
        const workingHoursData = Object.entries(barberProfile.workingHours).map(([day, hours]) => {
          if (hours && typeof hours === 'object' && 'start' in hours && 'end' in hours && 'enabled' in hours) {
            return {
              business_id: businessProfile.id,
              day_of_week: day,
              start_time: hours.start,
              end_time: hours.end,
              enabled: hours.enabled
            };
          }
          return null;
        }).filter(Boolean);

        if (workingHoursData.length > 0) {
          console.log('=== BUSINESS_HOURS PAYLOAD ===');
          console.log(JSON.stringify(workingHoursData, null, 2));
          
          const { error: hoursError } = await supabase
            .from('business_hours')
            .upsert(workingHoursData);

          if (hoursError) {
            console.error('=== BUSINESS_HOURS ERROR ===');
            console.error('Error code:', hoursError.code);
            console.error('Error message:', hoursError.message);
            console.error('Full error object:', JSON.stringify(hoursError, null, 2));
            // Don't throw - hours can be set later in dashboard
          } else {
            console.log('=== BUSINESS_HOURS SUCCESS ===');
          }
        }
      }

      // 4. Create or update user profile (non-blocking)
      const { data: existingUserProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking profile:', checkError);
      }

      if (!existingUserProfile) {
        const nameParts = (barberProfile.name || '').trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            first_name: firstName,
            last_name: lastName,
            role: 'business_owner'
          });

        if (insertError) {
          console.error('Error creating profile:', insertError);
          // Don't throw - profile is not critical for dashboard access
        }
      } else {
        const nameParts = (barberProfile.name || '').trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            first_name: firstName,
            last_name: lastName,
            role: 'business_owner'
          })
          .eq('id', user.id);

        if (updateError) {
          console.error('Error updating profile:', updateError);
          // Don't throw
        }
      }

      // 5. Save Stripe Connect account if connected (non-blocking)
      if (data.stripeAccountId) {
        const { error: stripeError } = await supabase
          .from('stripe_connect_accounts')
          .upsert({
            user_id: user.id,
            stripe_account_id: data.stripeAccountId,
            charges_enabled: data.stripeConnected,
            payouts_enabled: data.stripeConnected
          });

        if (stripeError) {
          console.error('Error saving Stripe account:', stripeError);
        }
      }

      toast.success('Business profile saved successfully');
      return businessProfile;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to save business profile';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized loadBusinessProfile to prevent effect re-runs
  const loadBusinessProfile = useCallback(async (): Promise<BusinessSignupData | null> => {
    debugLog.businessProfile('loadBusinessProfile START');
    setIsLoading(true);
    setError(null);

    try {
      debugLog.businessProfile('Getting current user');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        debugLog.businessProfile('No user found, returning null');
        setIsLoading(false);
        return null;
      }

      debugLog.businessProfile('User found, userId:', user.id);

      // Load business profile - this is the ONLY required query.
      // Use limit(1) + order defensively — a UNIQUE constraint on user_id now
      // exists, but limit(1) keeps the load safe against any edge cases.
      debugLog.businessProfile('Querying business_profiles');
      const { data: profileRows, error: profileError } = await supabase
        .from('business_profiles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const businessProfile = profileRows?.[0] ?? null;

      debugLog.businessProfile('business_profiles query complete - hasData:', !!businessProfile, 'error:', profileError?.code);

      if (profileError) {
        debugLog.warn('[BusinessProfile] Business profile query error:', profileError.code, profileError.message);
        setIsLoading(false);
        return null;
      }

      if (!businessProfile) {
        debugLog.businessProfile('No business profile found for user');
        setIsLoading(false);
        return null;
      }

      debugLog.businessProfile('Business profile loaded, id:', businessProfile.id);

      // Load services - OPTIONAL, use empty array on failure
      let services: any[] = [];
      debugLog.businessProfile('Querying business_services');
      const { data: servicesData, error: servicesError } = await supabase
        .from('business_services')
        .select('*')
        .eq('business_id', businessProfile.id);

      debugLog.businessProfile('business_services query complete - count:', servicesData?.length, 'error:', servicesError?.code);

      if (servicesError) {
        debugLog.warn('[BusinessProfile] Services query failed (non-blocking):', servicesError.message);
      } else {
        services = servicesData || [];
      }

      // Load working hours - OPTIONAL, use defaults on failure
      let workingHours: any[] = [];
      debugLog.businessProfile('Querying business_hours');
      const { data: hoursData, error: hoursError } = await supabase
        .from('business_hours')
        .select('*')
        .eq('business_id', businessProfile.id);

      debugLog.businessProfile('business_hours query complete - count:', hoursData?.length, 'error:', hoursError?.code);

      if (hoursError) {
        debugLog.warn('[BusinessProfile] Working hours query failed (non-blocking):', hoursError.message);
      } else {
        workingHours = hoursData || [];
      }

      // Load user profile - OPTIONAL, use defaults on failure
      let userProfile: any = null;
      debugLog.businessProfile('Querying profiles');
      const { data: profileData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      debugLog.businessProfile('profiles query complete - hasData:', !!profileData, 'error:', userError?.code);

      if (userError) {
        debugLog.warn('[BusinessProfile] User profile query failed (non-blocking):', userError.message);
      } else {
        userProfile = profileData;
      }

      // Construct BarberProfile from database data
      const barberProfile: BarberProfile = {
        name: userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : '',
        email: businessProfile.email,
        phone: businessProfile.phone || '',
        businessName: businessProfile.name,
        businessAddress: businessProfile.address || '',
        timezone: businessProfile.timezone,
        businessType: businessProfile.business_type,
        bookingUrl: businessProfile.booking_url || '',
        services: services.map(service => ({
          id: service.id,
          name: service.name,
          description: service.description || '',
          duration: service.duration,
          price: service.price,
          waitlistEnabled: service.waitlist_enabled || false,
          bufferTime: service.buffer_time || 0
        })),
        workingHours: {
          monday: { start: '09:00', end: '17:00', enabled: false },
          tuesday: { start: '09:00', end: '17:00', enabled: false },
          wednesday: { start: '09:00', end: '17:00', enabled: false },
          thursday: { start: '09:00', end: '17:00', enabled: false },
          friday: { start: '09:00', end: '17:00', enabled: false },
          saturday: { start: '10:00', end: '15:00', enabled: false },
          sunday: { start: '10:00', end: '15:00', enabled: false }
        },
        stripeConnected: businessProfile.stripe_connected || false,
        notificationsEnabled: businessProfile.notifications_enabled || false,
        remindersEnabled: businessProfile.reminders_enabled || false,
        googleCalendarConnected: businessProfile.google_calendar_connected || false,
        adminCancellationNotificationsEnabled: false,
        logo: businessProfile.logo_url || '',
        title: businessProfile.title || '',
        bio: businessProfile.bio || '',
        suiteNumber: businessProfile.suite_number || '',
        googleCalendarId: businessProfile.google_calendar_id || '',
        welcomeMessage: businessProfile.welcome_message || '',
        instagram: businessProfile.instagram || ''
      };

      // Map working hours from database (if available)
      if (workingHours.length > 0) {
        workingHours.forEach(hour => {
          const day = hour.day_of_week as keyof typeof barberProfile.workingHours;
          if (day in barberProfile.workingHours) {
            barberProfile.workingHours[day] = {
              start: hour.start_time,
              end: hour.end_time,
              enabled: hour.enabled || false
            };
          }
        });
      }

      // Convert back to BusinessSignupData format
      debugLog.businessProfile('Converting to BusinessSignupData format');
      const { mapFromBarberProfile } = await import('../lib/supabase-mapping');
      const result = mapFromBarberProfile(barberProfile);
      
      // Add businessId for analytics tracking
      (result as any).businessId = businessProfile.id;
      
      debugLog.businessProfile('loadBusinessProfile END - success, businessId:', businessProfile.id);
      setIsLoading(false);
      return result;
    } catch (err: any) {
      console.error('[BusinessProfile] Unexpected error in loadBusinessProfile:', err);
      const errorMessage = err.message || 'Failed to load business profile';
      setError(errorMessage);
      setIsLoading(false);
      // Show toast for unexpected errors (network issues, etc.)
      toast.error(errorMessage);
      return null;
    }
  }, []); // Empty deps - supabase client is stable

  return {
    saveBusinessProfile,
    loadBusinessProfile,
    isLoading,
    error
  };
};
